import * as React from "react";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import { useQuery } from "react-query";
import {
  JobRequest,
  JobRequestFileCategory,
  JobRequestsService,
  JobRequestStatus,
  MachineTranslationService
} from "gen/clients/llts";
import { LoadingPage } from "components/LoadingPage/LoadingPage";
import { ErrorPage } from "components/ErrorPage/ErrorPage";
import { LabelValue } from "components/LabelValue/LabelValue";
import { formatDate } from "utils/dateUtils";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslation } from "react-i18next";
import { useDialogState } from "hooks/useDialogState/useDialogState";
import { DownloadTranslatedFilesDialog } from "components/jobRequests/DownloadTranslatedFilesDialog/DownloadTranslatedFilesDialog";
import { JobRequestState } from "components/jobRequests/JobRequestFilter/JobRequestState";
import { JobRequestNameEditable } from "../../components/MtJobRequestDetailsPage/components/JobRequestNameEditable";
import { JobRequestUtils } from "utils/jobRequestUtils";
import { JobRequestFileList } from "components/jobRequests/JobRequestFileList/JobRequestFileList";
import { InfoIcon } from "components/InfoIcon/InfoIcon";

interface Props {
  jobRequestId: string;
  onReturnClick: () => void;
}

const AiToolboxDetailsPage: React.FC<Props> = ({ jobRequestId, onReturnClick }) => {
  const { t } = useTranslation();
  const [isDownloadEnhancedFilesDialogOpen, openDownloadEnhancedFilesDialog, closeDownloadEnhancedFilesDialog] =
    useDialogState();

  const {
    data: jobRequest,
    isLoading,
    error,
    refetch
  } = useQuery(["getJobRequest", jobRequestId], {
    queryFn: () => JobRequestsService.getJobRequest({ jobRequestId }),
    refetchInterval: (jr: JobRequest | undefined) => (JobRequestUtils.isInProgress(jr?.status) ? 10000 : false),
    cacheTime: 0
  });

  const { data: mtSettings } = useQuery(["getMtSettings"], {
    queryFn: MachineTranslationService.getMachineTranslationSettings
  });

  // Group source files by language
  const sourceFiles = React.useMemo(() => {
    if (!jobRequest) {
      return [];
    }

    return jobRequest.files?.filter(
      f =>
        f.category === JobRequestFileCategory.SOURCE_DOCUMENT ||
        f.category === JobRequestFileCategory.TRANSLATED_DOCUMENT
    );
  }, [jobRequest]);

  const enhancedFiles = React.useMemo(
    () =>
      jobRequest?.files
        ?.filter(f => f.category === JobRequestFileCategory.AI_POST_EDITED)
        ?.sort((f1, f2) =>
          f1.fileName
            .substring(0, f1.fileName.lastIndexOf("_"))
            .localeCompare(f2.fileName.substring(0, f2.fileName.lastIndexOf("_")))
        ),
    [jobRequest]
  );

  const onDownloadAllClick = React.useCallback(async () => {
    openDownloadEnhancedFilesDialog();
  }, [openDownloadEnhancedFilesDialog]);

  const getLanguageName = React.useCallback(
    (languageId: string | number | undefined) => {
      if (!languageId) {
        return null;
      }
      const languages = [...(jobRequest?.targetLanguages || [])];
      if (jobRequest?.sourceLanguage?.id === languageId) {
        languages.push(jobRequest.sourceLanguage);
      }
      const language = languages.find(l => l.id === languageId);
      return language?.name || `${languageId}`;
    },
    [jobRequest]
  );

  if (error) {
    return <ErrorPage apiError={error} />;
  }

  if (isLoading || !jobRequest) {
    return <LoadingPage />;
  }

  return (
    <Stack>
      <Stack direction="row" justifyItems="center" alignItems="center">
        <IconButton onClick={onReturnClick}>
          <ArrowBackIosIcon />
        </IconButton>
        <Box flexGrow={1}>
          <JobRequestNameEditable jobRequest={jobRequest} onChange={refetch} />
        </Box>
      </Stack>
      <Card sx={{ p: 2, mt: 1 }}>
        <Grid container={true} spacing={1}>
          <Grid item={true} xs={12} sm={6}>
            <Stack spacing={1}>
              <LabelValue
                label={t("machineTranslation.fileTranslation.requests.status")}
                value={
                  <Stack direction="row" spacing={1}>
                    <JobRequestState status={jobRequest.status} />
                    {jobRequest.status === JobRequestStatus.FAILED && (
                      <InfoIcon tooltip={t("machineTranslation.fileTranslation.requests.failedStatusInfo")} />
                    )}
                  </Stack>
                }
              />
              <LabelValue
                label={t("machineTranslation.fileTranslation.requests.submittedOn")}
                value={<Typography>{formatDate(jobRequest.createTime)}</Typography>}
              />
            </Stack>
          </Grid>
          <Grid item={true} xs={12} sm={6}>
            <Stack spacing={1}>
              <LabelValue
                label={t("machineTranslation.fileTranslation.requests.sourceLanguage")}
                value={<Typography>{jobRequest.sourceLanguage?.name}</Typography>}
              />
              <LabelValue
                label={t("machineTranslation.fileTranslation.requests.targetLanguages")}
                value={
                  <Typography>
                    {jobRequest.targetLanguages
                      ?.map(l => l.name)
                      .sort((l1, l2) => l1.localeCompare(l2))
                      .join(", ")}
                  </Typography>
                }
              />
            </Stack>
          </Grid>
        </Grid>
      </Card>

      <Grid container={true} spacing={2}>
        <Grid item={true} xs={12} sm={6}>
          <Card sx={{ mt: 2, height: 400, display: "flex", flexDirection: "column" }}>
            <CardHeader
              title={<Typography variant="h6">{t("machineTranslation.aiToolbox.inputFilesHeader")}</Typography>}
            />
            <CardContent sx={{ overflowY: "auto", pt: 0, flex: 1, display: "flex", flexDirection: "column" }}>
              <Box sx={{ flex: 1, overflowY: "auto" }}>
                {!sourceFiles || sourceFiles.length === 0 ? (
                  <Typography variant="body2">{t("machineTranslation.fileTranslation.requests.noFiles")}</Typography>
                ) : (
                  <JobRequestFileList
                    jobRequestId={jobRequestId}
                    files={sourceFiles}
                    groupByLanguage={true}
                    getLanguageName={getLanguageName}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item={true} xs={12} sm={6}>
          <Card sx={{ mt: 2, height: 400, display: "flex", flexDirection: "column" }}>
            <CardHeader
              title={
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h6">{t("machineTranslation.aiToolbox.deliverableFilesHeader")}</Typography>
                </Stack>
              }
            />
            <CardContent sx={{ overflowY: "auto", pt: 0, flex: 1, display: "flex", flexDirection: "column" }}>
              <Box sx={{ flex: 1, overflowY: "auto" }}>
                {!enhancedFiles || enhancedFiles.length === 0 ? (
                  <Typography variant="body2">
                    {JobRequestUtils.isInProgress(jobRequest.status)
                      ? t("machineTranslation.aiToolbox.deliverableFilesEmpty.inProgress")
                      : t("machineTranslation.aiToolbox.deliverableFilesEmpty.default")}
                  </Typography>
                ) : (
                  <JobRequestFileList
                    jobRequestId={jobRequestId}
                    files={enhancedFiles}
                    showAiLinguisticQualityScorePerFile={
                      mtSettings?.enableAiLinguisticQualityReview && mtSettings?.showAiLinguisticQualityScorePerFile
                    }
                    groupByLanguage={true}
                    getLanguageName={getLanguageName}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {enhancedFiles && enhancedFiles.length > 0 && !JobRequestUtils.isInProgress(jobRequest.status) && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <Button startIcon={<DownloadIcon />} variant="contained" onClick={onDownloadAllClick} size="small">
            {t("machineTranslation.fileTranslation.requests.downloadAllBtn")}
          </Button>
        </Box>
      )}

      {isDownloadEnhancedFilesDialogOpen && (
        <DownloadTranslatedFilesDialog
          jobRequestId={jobRequestId}
          zipFileName={`ai-toolbox-${jobRequestId}.zip`}
          onClose={closeDownloadEnhancedFilesDialog}
          onSuccess={closeDownloadEnhancedFilesDialog}
        />
      )}
    </Stack>
  );
};

export { AiToolboxDetailsPage };
